import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import CommunityService from '../lib/services/communityService';
import type { UserProfile, UserConnection } from '../types';
import { Spinner } from './Spinner';
import { toast } from 'react-hot-toast';
import SEO from './SEO';

// Criar uma instância do serviço
const communityService = new CommunityService();

const UserSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState<{[key: string]: UserConnection | null}>({});
  const [actionLoading, setActionLoading] = useState<{[key: string]: boolean}>({});

  // Buscar usuários quando a query mudar
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchTerm(query);
      searchUsers(query);
    }
  }, [searchParams]);

  const searchUsers = async (query: string) => {
    if (!query || query.trim().length < 2) return;
    
    setLoading(true);
    try {
      const results = await communityService.searchUsers(query);
      setUsers(results);
      
      // Se o usuário estiver logado, buscar status de conexão para cada usuário encontrado
      if (user) {
        const connectionStatuses: {[key: string]: UserConnection | null} = {};
        
        for (const profile of results) {
          if (profile.id !== user.id) {
            const connection = await communityService.getConnectionStatus(user.id, profile.id);
            connectionStatuses[profile.id] = connection;
          }
        }
        
        setConnections(connectionStatuses);
      }
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast.error('Erro ao buscar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (searchTerm.trim().length < 2) {
      toast.error('Digite pelo menos 2 caracteres para buscar');
      return;
    }
    
    setSearchParams({ q: searchTerm });
  };

  const handleSendRequest = async (userId: string) => {
    if (!user) {
      toast.error('Você precisa estar logado para adicionar amigos');
      return;
    }
    
    setActionLoading({ ...actionLoading, [userId]: true });
    
    try {
      const result = await communityService.sendConnectionRequest(user.id, userId);
      
      if (result.success) {
        toast.success('Solicitação de amizade enviada!');
        // Atualizar status da conexão
        const connection = await communityService.getConnectionStatus(user.id, userId);
        
        // Certifique-se de que o state seja completamente atualizado
        setConnections(prevConnections => ({
          ...prevConnections,
          [userId]: connection
        }));
      } else {
        toast.error(result.error || 'Erro ao enviar solicitação');
      }
    } catch (error) {
      console.error('Erro ao enviar solicitação:', error);
      toast.error('Erro ao enviar solicitação de amizade');
    } finally {
      setActionLoading({ ...actionLoading, [userId]: false });
    }
  };

  const handleCancelRequest = async (connectionId: string, userId: string) => {
    if (!user) return;
    
    setActionLoading({ ...actionLoading, [userId]: true });
    
    try {
      const result = await communityService.removeConnection(connectionId, user.id);
      
      if (result.success) {
        toast.success('Solicitação cancelada');
        // Usar callback para garantir que o estado seja atualizado corretamente
        setConnections(prevConnections => ({
          ...prevConnections,
          [userId]: null
        }));
      } else {
        toast.error(result.error || 'Erro ao cancelar solicitação');
      }
    } catch (error) {
      console.error('Erro ao cancelar solicitação:', error);
      toast.error('Erro ao cancelar solicitação');
    } finally {
      setActionLoading({ ...actionLoading, [userId]: false });
    }
  };

  const handleViewProfile = (username?: string) => {
    if (!username) return;
    navigate(`/profile/${username}`);
  };

  const renderConnectionButton = (profile: UserProfile) => {
    if (!user || user.id === profile.id) return null;
    
    const connection = connections[profile.id];
    const isLoading = actionLoading[profile.id];
    
    if (!connection) {
      return (
        <button
          onClick={() => handleSendRequest(profile.id)}
          disabled={isLoading}
          className="ml-auto bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm flex items-center dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          {isLoading ? (
            <Spinner size="small" />
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
              </svg>
              Adicionar
            </>
          )}
        </button>
      );
    }
    
    if (connection.status === 'pending') {
      // Se este usuário é o que enviou a solicitação
      if (connection.user_id === user.id) {
        return (
          <button
            onClick={() => handleCancelRequest(connection.id, profile.id)}
            disabled={isLoading}
            className="ml-auto bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded-md text-sm flex items-center dark:bg-yellow-500 dark:hover:bg-yellow-600"
          >
            {isLoading ? (
              <Spinner size="small" />
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                </svg>
                Solicitação enviada
              </>
            )}
          </button>
        );
      } else {
        return (
          <div className="ml-auto flex items-center text-sm text-gray-600 dark:text-gray-300">
            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded dark:bg-yellow-700 dark:text-yellow-100">Solicitação recebida</span>
          </div>
        );
      }
    }
    
    if (connection.status === 'accepted') {
      return (
        <div className="ml-auto flex items-center text-sm text-gray-600 dark:text-gray-300">
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded dark:bg-green-700 dark:text-green-100">Amigos</span>
        </div>
      );
    }
    
    return null;
  };

  return (
    <>
      <SEO 
        title="Buscar Usuários"
        description="Encontre e conecte-se com outros usuários da comunidade Boardverso."
      />
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6 dark:text-gray-100">Buscar Usuários</h1>
        
        {/* Formulário de busca */}
        <div className="mb-8">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Digite um nome de usuário ou email..."
              className="flex-grow px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              minLength={2}
            />
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Buscar
            </button>
          </form>
          <p className="text-sm text-gray-500 mt-2 dark:text-gray-400">
            Digite pelo menos 2 caracteres para buscar usuários.
          </p>
        </div>
        
        {/* Resultados da busca */}
        <div className="mt-6">
          {loading ? (
            <div className="flex flex-col items-center py-10">
              <Spinner size="large" />
              <p className="mt-4 text-gray-600 dark:text-gray-300">Buscando usuários...</p>
            </div>
          ) : searchParams.has('q') && users.length > 0 ? (
            <div className="space-y-4">
              {users.map(profile => (
                <div 
                  key={profile.id} 
                  className="bg-white rounded-lg shadow-md p-4 flex items-center dark:bg-gray-800 dark:border dark:border-gray-700"
                >
                  {/* Avatar */}
                  <div 
                    className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 cursor-pointer dark:bg-gray-700"
                    onClick={() => handleViewProfile(profile.username)}
                  >
                    {profile.avatar_url ? (
                      <img 
                        src={profile.avatar_url} 
                        alt={profile.username} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-300 dark:bg-gray-600">
                        <span className="text-xl font-bold text-gray-600 dark:text-gray-300">
                          {profile.username?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Informações */}
                  <div className="ml-4 flex-grow">
                    <h3 
                      className="font-medium text-blue-600 hover:underline cursor-pointer dark:text-blue-400 dark:hover:text-blue-300"
                      onClick={() => handleViewProfile(profile.username)}
                    >
                      {profile.full_name || profile.username}
                    </h3>
                    
                    {profile.full_name && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">@{profile.username}</p>
                    )}
                    
                    {profile.bio && (
                      <p className="text-sm text-gray-700 mt-1 line-clamp-1 dark:text-gray-300">{profile.bio}</p>
                    )}
                  </div>
                  
                  {/* Botão de ação (adicionar amigo, etc) */}
                  {renderConnectionButton(profile)}
                </div>
              ))}
            </div>
          ) : searchParams.has('q') ? (
            <div className="text-center py-10 border rounded-lg bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="text-xl font-medium text-gray-600 dark:text-gray-200">Nenhum usuário encontrado</h3>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                Não encontramos usuários correspondentes à sua busca "{searchParams.get('q')}".
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default UserSearchPage; 